/**
 * Side drawer for one line of "Where AI processing cost went".
 *
 * The row says how much a driver cost; the next question is what it is made of.
 * Everything shown is already in the billing summary — the per-service token
 * costs and the infrastructure line items — so opening it costs no request.
 *
 * Portalled to document.body for the same reason as DetailDrawer: the page's
 * entrance animations apply a transform, which would otherwise make an
 * ancestor the containing block of this `fixed` panel.
 */
import { useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ArrowRight, X } from 'lucide-react'
import { useBodyScrollLock, usePrefersReducedMotion } from './BillingPrimitives'
import { formatCompact, formatInt, formatUsdAs } from '../../utils/billing'

const SERVICE_LABELS = {
  normalisation: { name: 'Field normalisation', plain: 'Tidying extracted values into your field formats' },
  llm_fallback: { name: 'LLM fallback', plain: 'Second pass when the first extraction is low confidence' },
  email: { name: 'Email analysis', plain: 'Reading the email body to route and title the request' },
  ai_learning: { name: 'AI learning', plain: 'Working out rules from the corrections made in review' },
}

/** The parts a driver is made of, as { id, name, plain, cost, detail }. */
function buildParts(driver, summary, scope) {
  const costs = summary?.estimated_costs?.[scope] || {}
  const pricing = summary?.estimated_costs?.pricing || {}

  if (driver.id === 'ai') {
    return Object.entries(costs.services || {})
      .map(([key, svc]) => ({
        id: key,
        name: SERVICE_LABELS[key]?.name || key,
        plain: SERVICE_LABELS[key]?.plain || '',
        cost: svc.estimated_cost_usd || 0,
        detail: `${formatCompact(svc.total_tokens)} tokens`,
      }))
      .sort((a, b) => b.cost - a.cost)
  }

  if (driver.id === 'infra') {
    return Object.entries(summary?.infrastructure_costs?.services || {})
      .map(([key, svc]) => ({
        id: key,
        name: svc.name,
        plain: svc.description,
        cost: svc.monthly_usd || 0,
        detail: svc.category,
      }))
      .filter(p => p.cost > 0)
      .sort((a, b) => b.cost - a.cost)
  }

  // Document processing is the per-page fee on the pages Azure charged for,
  // plus the tokens of the model Content Understanding runs. Older payloads
  // carry no split, and show the pages line alone.
  const modelTokens = (costs.cu_input_tokens || 0) + (costs.cu_output_tokens || 0)
  return [{
    id: 'pages',
    name: 'Pages scanned',
    plain: 'Azure Content Understanding, billed once per page of each PDF',
    cost: driver.cost,
    detail: `${formatInt(costs.pages_billed)} pages × ${formatUsdAs(pricing.per_page_usd || 0, 'USD', 4)}`
      + (modelTokens > 0 ? ` + ${formatCompact(modelTokens)} model tokens` : ''),
  }]
}

export default function CostDriverDrawer({ driver, share, summary, scope, periodLabel, onClose, onExplore, displayCurrency }) {
  const panelRef = useRef(null)
  const reduced = usePrefersReducedMotion()
  const open = Boolean(driver)
  useBodyScrollLock(open)

  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const previous = document.activeElement
    panelRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus?.()
    }
  }, [open, onClose])

  const parts = useMemo(() => (driver ? buildParts(driver, summary, scope) : []), [driver, summary, scope])

  if (!open) return null

  const partMax = Math.max(0.0001, ...parts.map(p => p.cost))
  const money = v => formatUsdAs(v, displayCurrency)

  return createPortal(
    <>
      <button
        type="button"
        aria-label="Close details"
        onClick={onClose}
        className={`fixed inset-0 z-40 bg-gray-900/50 dark:bg-black/70 backdrop-blur-[2px] ${reduced ? '' : 'animate-fade-in'}`}
      />
      <aside
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`${driver.name} details`}
        className={`fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[480px] bg-white dark:bg-gray-900
                    border-l border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col focus:outline-none
                    ${reduced ? '' : 'animate-drawer-in'}`}
      >
        <header className="flex items-start gap-3 p-5 border-b border-gray-200 dark:border-gray-800">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.09em] text-gray-400 dark:text-gray-500">
              Cost driver
            </p>
            <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 mt-1">{driver.name}</h2>
            <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5">{driver.plain}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                       hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: scope === 'current_month' ? 'This billing month' : periodLabel, value: money(driver.cost) },
              { label: driver.fixed ? 'Billing' : 'Of usage cost', value: driver.fixed ? 'Fixed' : `${Math.round(share * 100)}%` },
              { label: 'Volume', value: driver.unit },
            ].map(stat => (
              <div key={stat.label} className="rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2.5 min-w-0">
                <p className="text-[17px] font-bold text-gray-900 dark:text-gray-100 tabular-nums leading-tight truncate" title={stat.value}>
                  {stat.value}
                </p>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mt-1">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>

          <section>
            <h3 className="text-[10px] font-semibold uppercase tracking-[0.09em] text-gray-400 dark:text-gray-500 mb-2">
              What makes it up
            </h3>
            {parts.length === 0 ? (
              <p className="text-[12px] text-gray-500 dark:text-gray-400">Nothing recorded for this {scope === 'current_month' ? 'billing month' : 'period'} yet.</p>
            ) : (
              <div className="space-y-2.5">
                {parts.map(part => (
                  <div key={part.id} className="rounded-lg border border-gray-200 dark:border-gray-800 p-3.5">
                    <div className="flex items-baseline gap-3">
                      <p className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">{part.name}</p>
                      <p className="ml-auto text-[13px] font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                        {money(part.cost)}
                      </p>
                    </div>
                    {part.plain && (
                      <p className="text-[11.5px] text-gray-500 dark:text-gray-400 mt-0.5">{part.plain}</p>
                    )}
                    <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 mt-2.5 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-[width] duration-500 ease-out"
                        style={{ width: `${Math.max(2, (part.cost / partMax) * 100)}%`, backgroundColor: driver.color }}
                      />
                    </div>
                    {part.detail && (
                      <p className="text-[10.5px] text-gray-400 dark:text-gray-500 mt-1.5 tabular-nums">{part.detail}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {driver.fixed && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-800 pt-3">
              A flat monthly estimate of the Azure resources behind Xtract. It does not move with how much you
              process, and is not multiplied across longer periods.
            </p>
          )}
        </div>

        <footer className="p-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40">
          <button
            type="button"
            onClick={onExplore}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-navy-700 dark:text-navy-300
                       hover:gap-2.5 transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 rounded"
          >
            Full cost breakdown
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </footer>
      </aside>
    </>,
    document.body,
  )
}
